import { OnboardingStatus, MeUserDto } from '@interfaces/models';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser, useAuth } from '@shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { currentUserDetails } from '@api/users';
import { ssManager } from '@shared/utils';
import { Routes } from '@shared/enums';
import { useEffect } from 'react';

export const useProtectedRoute = (): {
  isLoading: boolean;
  isError: boolean;
  data?: MeUserDto;
} => {
  const { user, setUser, userLogout } = useCurrentUser();
  const { login, isAuthenticated } = useAuth();
  const { companyId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryFn: currentUserDetails,
    queryKey: ['current-user'],
    enabled: !user,
    retry: false,
  });

  useEffect(() => {
    /* if token is not valid or token is not present user is not logged in */
    /* We want to logout user first, and then login again (redirect to login page) */
    if (isError) {
      if (isAuthenticated) {
        userLogout();
      } else {
        login();
      }
    }
  }, [isError, userLogout, login, isAuthenticated]);

  useEffect(() => {
    /* once user is fetched, set it to context */
    if (!data) return;
    setUser(data);

    /* if user has not accepted all required consents, redirect to accept consents route */
    if (!data?.hasAllRequiredConsentsAccepted) {
      navigate(Routes.ACCEPT_CONSENTS, { replace: true });
      return;
    }

    /* if user is onboarding, redirect to onboarding route */
    if (data?.onboardingStatus === OnboardingStatus.IN_PROGRESS) {
      // TODO: This is temporary solution - only for payment status, we should always redirect to onboarding route
      if (!location.pathname.includes(Routes.ONBOARDING)) navigate(Routes.ONBOARDING);
      return;
    }

    /* if user has only one company, and user is not on company route, redirect to company route */
    if (!companyId && data?.companies.length === 1) {
      navigate(`/${data.companies[0]?.id}`);
      return;
    }

    if (!companyId && ssManager.has('COMPANY_ID')) {
      const lsCompanyId = ssManager.get('COMPANY_ID');
      if (data?.companies.find((company) => company.id === lsCompanyId)) {
        navigate(`/${lsCompanyId}`);
        return;
      }
    }

    if (data?.companies.length === 0) {
      navigate(Routes.NOT_FOUND);
      throw new Error('No companies found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    /* sync companyId with local storage */
    if (!!companyId) ssManager.set('COMPANY_ID', companyId);
  }, [companyId]);

  return { isLoading, isError, data };
};
